import type { ActionType, ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { Button, Space, Tag } from 'antd';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { ExamStatusEnum, MyExamItemDTO, examAttemptController } from '../../api';
import { useCompany } from '../../hooks/company';
import ExamResultDrawer from './ExamResultDrawer';
import ExamTakingDrawer from './ExamTakingDrawer';

// 以服务端时间推导考试对当前用户的状态
const deriveStatus = (item: MyExamItemDTO) => {
  if (item.status === ExamStatusEnum.CLOSED) {
    return { text: '已关闭', color: 'default', canTake: false };
  }
  const now = item.serverNow ? dayjs(item.serverNow) : dayjs();
  if (item.startAt && now.isBefore(dayjs(item.startAt))) {
    return { text: '未开始', color: 'blue', canTake: false };
  }
  if (item.endAt && now.isAfter(dayjs(item.endAt))) {
    return { text: '已截止', color: 'default', canTake: false };
  }
  if ((item.usedAttempts || 0) >= (item.maxAttempts || 1) && !item.hasInProgress) {
    return { text: '已完成', color: 'green', canTake: false };
  }
  return { text: '进行中', color: 'processing', canTake: true };
};

export default () => {
  const { id: companyId } = useCompany();
  const ref = useRef<ActionType>();
  const [takingExam, setTakingExam] = useState<MyExamItemDTO>();
  const [resultExam, setResultExam] = useState<MyExamItemDTO>();

  const columns: ProColumns<MyExamItemDTO>[] = [
    { title: '考试名称', dataIndex: 'title', ellipsis: true },
    {
      title: '状态',
      width: 90,
      render: (_, record) => {
        const status = deriveStatus(record);
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: '考试时间',
      width: 260,
      render: (_, record) => {
        if (!record.startAt && !record.endAt) {
          return '不限';
        }
        return `${record.startAt || '不限'} ~ ${record.endAt || '不限'}`;
      },
    },
    {
      title: '限时',
      width: 90,
      render: (_, record) =>
        record.durationMinutes ? `${record.durationMinutes} 分钟` : '不限',
    },
    {
      title: '题数 / 总分 / 及格线',
      width: 150,
      render: (_, record) =>
        `${record.questionCount} / ${record.totalScore} / ${record.passScore}`,
    },
    {
      title: '剩余次数',
      width: 90,
      render: (_, record) =>
        Math.max(0, (record.maxAttempts || 1) - (record.usedAttempts || 0)),
    },
    {
      title: '我的最高分',
      width: 110,
      render: (_, record) => {
        if (record.myBestScore === undefined || record.myBestScore === null) {
          return '-';
        }
        return (
          <Space size={4}>
            {record.myBestScore}
            {record.myBestPass ? (
              <Tag color="green">通过</Tag>
            ) : (
              <Tag color="red">未通过</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '操作',
      valueType: 'option',
      width: 180,
      render: (_, record) => {
        const status = deriveStatus(record);
        const actions = [];
        if (status.canTake) {
          actions.push(
            <Button
              key="take"
              type="primary"
              size="small"
              onClick={() => setTakingExam(record)}
            >
              {record.hasInProgress ? '继续答题' : '开始考试'}
            </Button>,
          );
        }
        if ((record.usedAttempts || 0) > 0) {
          actions.push(
            <a key="result" onClick={() => setResultExam(record)}>
              查看成绩
            </a>,
          );
        }
        return <Space>{actions.length ? actions : '-'}</Space>;
      },
    },
  ];

  return (
    <>
      <ProTable<MyExamItemDTO>
        actionRef={ref}
        rowKey="examId"
        columns={columns}
        search={false}
        pagination={{ pageSizeOptions: [20, 50, 100] }}
        request={async ({ current, pageSize }) => {
          const data = await examAttemptController.myList(
            companyId,
            current,
            pageSize,
          );
          return { data: data.list, success: true, total: data.total };
        }}
        headerTitle="我的考试"
      />
      {takingExam && (
        <ExamTakingDrawer
          exam={takingExam}
          onClose={(finished) => {
            setTakingExam(undefined);
            if (finished) {
              ref.current?.reload();
            }
          }}
        />
      )}
      {resultExam && (
        <ExamResultDrawer
          exam={resultExam}
          onClose={() => setResultExam(undefined)}
        />
      )}
    </>
  );
};
